import React, { useMemo } from 'react';

import { classnames, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiArrowDownThick, mdiArrowUpThick } from '@lumapps/lumx/icons';
import { Button, Emphasis, Theme, Tooltip } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';
import { isConnected as isConnectedSelector } from '@lumapps/user/ducks/selectors';

import { VOTES } from '../../keys';

import './index.scss';

export interface VoteButtonProps {
    voteCount: number;
    handleVote: () => void;
    isCompact: boolean;
    isSelected: boolean;
    scope: string;
    action: 'upvote' | 'downvote';
    isPreviewOnly?: boolean;
    theme?: Theme;
    className?: string;
}

const CLASSNAME = 'reactions-votes';

export const VoteButton: React.FC<VoteButtonProps> = ({
    voteCount,
    handleVote,
    isCompact,
    isSelected,
    isPreviewOnly,
    action,
    theme,
    scope,
    className,
}) => {
    const isUserConnected = useSelector(isConnectedSelector);

    const { element } = useClassnames(CLASSNAME);

    const { get } = useDataAttributes(scope);

    const { pluralize, translateAndReplace, translateKey } = useTranslate();

    const { leftIcon, pluralizeKey, labelKey } = useMemo(
        () =>
            action === 'upvote'
                ? { leftIcon: mdiArrowUpThick, pluralizeKey: VOTES.PLURALIZED_UPVOTES, labelKey: VOTES.UPVOTE }
                : { leftIcon: mdiArrowDownThick, pluralizeKey: VOTES.PLURALIZED_DOWNVOTES, labelKey: VOTES.DOWNVOTE },
        [action],
    );

    const pluralizedVotes = translateAndReplace(pluralize(pluralizeKey, voteCount || 1), {
        number: voteCount,
    });

    if (!isUserConnected) {
        return null;
    }

    return (
        <Tooltip label={translateKey(labelKey)} ariaLinkMode="aria-describedby" closeMode="hide">
            <Button
                theme={theme}
                emphasis={Emphasis.medium}
                leftIcon={leftIcon}
                onClick={handleVote}
                isSelected={isSelected}
                className={classnames(
                    element(action, {
                        'is-preview-only': Boolean(isPreviewOnly),
                    }),
                    className,
                )}
                aria-label={isCompact ? pluralizedVotes : undefined}
                {...get({ element: 'button', action })}
            >
                {isCompact ? voteCount.toString() : pluralizedVotes}
            </Button>
        </Tooltip>
    );
};
